import React, { useRef } from 'react';
import { Formik } from 'formik';
import { string, object } from 'yup';
import Modal from 'mf-react-ui/Modal';
import { H2, P } from 'mf-react-ui/Typography';
import Button from 'mf-react-ui/Button';
import Link from 'mf-react-ui/Link';
import FormField from '../../../../components/FormField/FormField';
import TextField from '../../../../components/TextField/TextField';
import TextareaField from '../../../../components/TextareaField/TextareaField';
import isDisabled from './utils/isDisabled';
import { AppRoute } from '../../../../const';
import { Props, HandleFieldWithGABlurParams } from './types';
import { EventCat, EventGTM, EventId, EventName, trackEventBound } from '../../../../utils/ga';
import '../../style.scss';
import './FormSendGiftModal.scss';

const DEFAULT_MESSAGE: string =
  'С праздником! Пусть будет больше поводов для отличного настроения.';

const FormSendGiftModal: React.FC<Props> = (props) => {
  const { isOpen, partner, serviceName, price, onClose, hideModal, subscribeGift } = props;
  const fieldsSentEarlyToGA = useRef<{ [index: string]: any }>({});

  const handleCloseButtonClick = (): void => {
    onClose();
    hideModal();
  };

  const handleFieldWithGABlur = ({
    event,
    caption,
    fieldName,
    value,
    errors,
    cb,
  }: HandleFieldWithGABlurParams): void => {
    cb(event);

    if (
      !Object.prototype.hasOwnProperty.call(errors, fieldName) &&
      fieldsSentEarlyToGA.current[fieldName] !== value
    ) {
      trackEventBound(
        EventGTM.MakeConversions,
        EventId.SendInputToGAInFormSendGiftModal,
        EventCat.Gift,
        EventName.SendInput,
        `${partner} / ${caption} / Ввод данных`
      );
      fieldsSentEarlyToGA.current[fieldName] = value;
    }
  };

  return (
    <Modal
      className="modal_form-send-gift"
      titleText="Форма"
      initialFocus=".modal__close"
      size="l"
      isOpen={isOpen}
      onCloseButtonClick={handleCloseButtonClick}
    >
      <div className="dialog-form-send-gift">
        <H2 align="center" className="dialog-form-send-gift__title">
          Оформление
        </H2>
        <P align="center" className="dialog-form-send-gift__description">
          Заполните поля ниже и мы сразу же отправим{' '}подарок{' '}получателю
        </P>
        <div className="dialog-form-send-gift__attention-wrapper">
          <P className="dialog-form-send-gift__attention">
            Активировать подарок сможет только абонент МегаФона. Проверить, является ли Получатель
            абонентом МегаФона, можно по номеру{' '}
            <Link className="dialog-form-send-gift__check-phone" href="tel:*629%23">
              *629#
            </Link>
          </P>
        </div>
        <Formik
          initialValues={{
            senderName: '',
            recipientName: '',
            recipientEmail: '',
            message: DEFAULT_MESSAGE,
          }}
          validationSchema={object().shape({
            recipientName: string().min(2).required(),
            recipientEmail: string().email().required(),
            message: string().min(2).required(),
          })}
          onSubmit={(values) => {
            const newValues: {
              senderName?: string;
              recipientName: string;
              recipientEmail: string;
              message: string;
            } = { ...values };

            if (newValues.senderName !== undefined && newValues.senderName.trim() === '') {
              delete newValues.senderName;
            }

            subscribeGift({
              ...newValues,
              message: newValues.message || DEFAULT_MESSAGE,
              partner,
              serviceName,
            });
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
            <form className="form form_for_send-gift" onSubmit={handleSubmit}>
              <div className="form__field">
                <FormField caption="Ваше имя">
                  <TextField
                    name="senderName"
                    maxLength={50}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.senderName}
                  />
                </FormField>
              </div>
              <div className="form__field">
                <FormField caption="Имя получателя">
                  <TextField
                    name="recipientName"
                    maxLength={50}
                    onChange={handleChange}
                    onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                      handleFieldWithGABlur({
                        event,
                        caption: 'Имя получателя',
                        fieldName: 'recipientName',
                        value: values.recipientName,
                        errors,
                        cb: handleBlur,
                      })
                    }
                    value={values.recipientName}
                  />
                </FormField>
              </div>
              <div className="form__field">
                <FormField
                  caption="На какой email отправить подарок?"
                  comment="Введите свой, если хотите отправить подарок позже"
                  className="form-field_for_recipient-email"
                >
                  <TextField
                    type="email"
                    name="recipientEmail"
                    onChange={handleChange}
                    onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                      handleFieldWithGABlur({
                        event,
                        caption: 'На какой email отправить подарок?',
                        fieldName: 'recipientEmail',
                        value: values.recipientEmail,
                        errors,
                        cb: handleBlur,
                      })
                    }
                    value={values.recipientEmail}
                  />
                </FormField>
              </div>
              <div className="form__field">
                <FormField caption="Текст поздравления">
                  <TextareaField
                    name="message"
                    maxLength={3000}
                    onChange={handleChange}
                    // @ts-ignore
                    onBlur={(event: React.FocusEvent<HTMLTextAreaElement>) =>
                      handleFieldWithGABlur({
                        event,
                        caption: 'Текст поздравления',
                        fieldName: 'message',
                        value: values.message,
                        errors,
                        cb: handleBlur,
                      })
                    }
                    value={values.message}
                  />
                </FormField>
              </div>
              <div className="form__price-wrapper">
                <P className="form__price">{price} ₽</P>
                <P className="form__price-note">
                  Единоразово будут списаны
                  <br /> с вашего счёта телефона
                </P>
              </div>
              <div className="form__actions">
                <Button
                  className="form__action"
                  type="submit"
                  disabled={isDisabled(errors, touched)}
                >
                  Отправить подарок
                </Button>
              </div>
            </form>
          )}
        </Formik>
        <P className="dialog-form-send-gift__note" align="center">
          С подробными условиями акции
          <br /> можно ознакомиться{' '}
          <Link view="context" href={AppRoute.LEGAL} external>
            здесь
          </Link>
        </P>
      </div>
    </Modal>
  );
};

export default FormSendGiftModal;
